import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Public APIs
export const publicAPI = {
  // Products
  getProducts: (params = {}) => api.get('/products', { params }),
  getProduct: (id) => api.get(`/products/${id}`),
  getProductBySku: (sku) => api.get(`/products/sku/${sku}`),
  getFilters: () => api.get('/filters'),
  
  // Categories
  getCategories: (division) => api.get('/categories', { params: { division } }),
  
  // RFQ
  submitRFQ: (data) => api.post('/rfq', data),
  
  // Contact
  submitContact: (data) => api.post('/contact', data),
  
  // Blog
  getBlogPosts: (params = {}) => api.get('/blog', { params }),
  getBlogPost: (slug) => api.get(`/blog/${slug}`),
  
  // SEO
  getSEO: (pageKey) => api.get(`/seo/${pageKey}`),
  
  // Site Content
  getContent: () => api.get('/content'),
  
  // Stats
  getStats: () => api.get('/stats'),
  
  // Resources & Downloads
  getResources: () => api.get('/resources'),
  submitDownloadLead: (data) => api.post('/download-lead', data),
  
  // Jobs & Careers
  getJobs: () => api.get('/jobs'),
  submitJobApplication: (formData) => api.post('/job-application', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  
  // Callback
  submitCallback: (data) => api.post('/callback-request', data),
  
  // Health
  healthCheck: () => api.get('/health'),
};

// Admin APIs
export const adminAPI = {
  // Auth
  login: (data) => api.post('/admin/login', data),
  getProfile: () => api.get('/admin/me'),
  
  // Dashboard
  getDashboard: () => api.get('/admin/dashboard'),
  
  // Products
  getProducts: (params = {}) => api.get('/admin/products', { params }),
  createProduct: (data) => api.post('/admin/products', data),
  updateProduct: (id, data) => api.put(`/admin/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`),
  importProducts: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/products/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  // RFQs
  getRFQs: (params = {}) => api.get('/admin/rfqs', { params }),
  updateRFQ: (id, status, notes) => api.put(`/admin/rfqs/${id}`, null, { params: { status, notes } }),
  
  // Blog
  getBlogPosts: () => api.get('/admin/blog'),
  createBlogPost: (data) => api.post('/admin/blog', data),
  updateBlogPost: (id, data) => api.put(`/admin/blog/${id}`, data),
  deleteBlogPost: (id) => api.delete(`/admin/blog/${id}`),
  
  // SEO
  getAllSEO: () => api.get('/admin/seo'),
  updateSEO: (pageKey, data) => api.put(`/admin/seo/${pageKey}`, data),
  
  // Site Content
  getContent: () => api.get('/admin/content'),
  updateContent: (data) => api.post('/admin/content', data),
  
  // Resources
  getResources: () => api.get('/admin/resources'),
  createResource: (formData) => api.post('/admin/resources', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteResource: (id) => api.delete(`/admin/resources/${id}`),
  getDownloadLeads: () => api.get('/admin/download-leads'),
  
  // Image Upload
  uploadImage: (file, folder = 'singhania-products') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    return api.post('/admin/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  // Jobs
  getJobs: () => api.get('/admin/jobs'),
  createJob: (data) => api.post('/admin/jobs', data),
  updateJob: (id, data) => api.put(`/admin/jobs/${id}`, data),
  deleteJob: (id) => api.delete(`/admin/jobs/${id}`),
  getApplications: () => api.get('/admin/applications'),
  
  // Security
  changePassword: (data) => api.post('/admin/change-password', data),
  
  // Search Stats
  getSearchStats: () => api.get('/admin/search-stats'),
  
  // Callbacks
  getCallbacks: () => api.get('/admin/callbacks'),
  updateCallback: (id, status) => api.put(`/admin/callbacks/${id}`, null, { params: { status } }),
  
  // Categories
  getCategories: () => api.get('/admin/categories'),
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (id, data) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`),
  
  // CMS Capabilities
  getCapabilities: () => api.get('/admin/cms/capabilities'),
  updateCapabilities: (data) => api.put('/admin/cms/capabilities', data),
  
  // Contacts
  getContacts: () => api.get('/admin/contacts'),

  // Customers
  getCustomers: (params = {}) => api.get('/admin/customers', { params }),
  createCustomer: (data) => api.post('/admin/customers', data),
  updateCustomer: (id, data) => api.put(`/admin/customers/${id}`, data),
  deleteCustomer: (id) => api.delete(`/admin/customers/${id}`),
  importCustomers: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/customers/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  importRFQCustomers: () => api.post('/admin/customers/import-rfqs'),

  // Newsletters
  getNewsletters: () => api.get('/admin/newsletters'),
  createNewsletter: (data) => api.post('/admin/newsletters', data),
  updateNewsletter: (id, data) => api.put(`/admin/newsletters/${id}`, data),
  deleteNewsletter: (id) => api.delete(`/admin/newsletters/${id}`),
  approveNewsletter: (id) => api.post(`/admin/newsletters/${id}/approve`),
  scheduleNewsletter: (id) => api.post(`/admin/newsletters/${id}/schedule`),
  pauseNewsletter: (id) => api.post(`/admin/newsletters/${id}/pause`),
  sendNewsletter: (id) => api.post(`/admin/newsletters/${id}/send`),
  testNewsletter: (id, email) => api.post(`/admin/newsletters/${id}/test`, { email }),
  previewNewsletter: (data) => api.post('/admin/newsletters/preview', data),
  generateNewsletterInfographic: (data) => api.post('/admin/newsletters/ai-infographic', data),

  // Newsletter Studio
  getRecipientLists: () => api.get('/admin/recipient-lists'),
  createRecipientList: (data) => api.post('/admin/recipient-lists', data),
  updateRecipientList: (id, data) => api.put(`/admin/recipient-lists/${id}`, data),
  deleteRecipientList: (id) => api.delete(`/admin/recipient-lists/${id}`),
  importRecipientList: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/recipient-lists/${id}/import`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getSenderProfiles: () => api.get('/admin/sender-profiles'),
  createSenderProfile: (data) => api.post('/admin/sender-profiles', data),
  updateSenderProfile: (id, data) => api.put(`/admin/sender-profiles/${id}`, data),
  deleteSenderProfile: (id) => api.delete(`/admin/sender-profiles/${id}`),
  getNewsletterTemplates: () => api.get('/admin/newsletter-templates'),
  createNewsletterTemplate: (data) => api.post('/admin/newsletter-templates', data),
  updateNewsletterTemplate: (id, data) => api.put(`/admin/newsletter-templates/${id}`, data),
  deleteNewsletterTemplate: (id) => api.delete(`/admin/newsletter-templates/${id}`),

  // Communications
  getCommunicationSettings: () => api.get('/admin/communications/settings'),
  updateCommunicationSettings: (data) => api.put('/admin/communications/settings', data),
  testCommunications: () => api.post('/admin/communications/test'),
  getCommunicationLogs: () => api.get('/admin/communications/logs'),
};

export const streamNewsletterDraft = async (payload, onDelta) => {
  const response = await fetch(`${API_BASE}/admin/newsletters/ai-draft`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok || !response.body) throw new Error('AI draft generation failed');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop() || '';
    for (const event of events) {
      const line = event.split('\n').find((item) => item.startsWith('data: '));
      if (!line) continue;
      const data = JSON.parse(line.slice(6));
      if (data.error) throw new Error(data.error);
      if (data.delta) onDelta(data.delta);
    }
  }
};

// Setup API
export const setupAPI = {
  initialize: () => api.post('/setup/init'),
};

export default api;
